<template>
  <div class="reservation-details-page">
    <Header />

    <main class="reservation-details-page__body">
      <div class="reservation-details-page__toolbar">
        <div class="reservation-details-page__heading">
          <h1 class="reservation-details-page__title">Reservation details</h1>
          <p
            v-if="ReservationDetails.reference_number"
            class="reservation-details-page__ref"
          >
            {{ ReservationDetails.reference_number }}
          </p>
        </div>
        <div
          class="reservation-details-page__actions reservation-details-page__actions--top"
        >
          <a
            v-if="
              ReservationDetails.status === 'Reserved' ||
                ReservationDetails.status === 'Paid' ||
                ReservationDetails.status === 'PartiallyPaid'
            "
            role="button"
            href="#"
            class="reservation-details-page__btn reservation-details-page__btn--print"
            @click.prevent="CompleteBooking(ReservationDetails.id)"
          >
            <i class="icon_printer" aria-hidden="true"></i>
            Print
          </a>
          <button
            v-if="canCheckIn"
            type="button"
            class="reservation-details-page__action-btn reservation-details-page__action-btn--primary"
            :disabled="checkingIn || checkingOut"
            @click="checkInGuest"
          >
            {{ checkingIn ? 'Checking in…' : 'Check in guest' }}
          </button>
          <button
            v-if="canCheckOut"
            type="button"
            class="reservation-details-page__action-btn"
            :disabled="checkingIn || checkingOut"
            @click="checkOutGuest"
          >
            {{ checkingOut ? 'Checking out…' : 'Check out guest' }}
          </button>
          <button
            type="button"
            class="reservation-details-page__back"
            @click="goBack"
          >
            Back
          </button>
        </div>
      </div>

      <div class="row justify-content-center">
        <div class="col-lg-12">
          <div class="box_booking_2">
            <div class="main back-white">
              <div class="row summary-cards-row">
                <div class="col-lg-4">
                  <div class="summary-card">
                    <h6 class="summary-card__title">Reservation</h6>
                    <dl class="detail-list">
                      <div class="detail-list__row">
                        <dt>Reference</dt>
                        <dd class="detail-list__ref">
                          {{ ReservationDetails.reference_number }}
                        </dd>
                      </div>
                      <div class="detail-list__row">
                        <dt>Payment status</dt>
                        <dd>
                          <span
                            class="payment-status-badge"
                            :class="paymentStatusClass"
                          >
                            {{ formatPaymentStatus(ReservationDetails.status) }}
                          </span>
                        </dd>
                      </div>
                      <div class="detail-list__row detail-list__row--field">
                        <dt>Booking status</dt>
                        <dd>
                          <Multiselect
                            v-model="selectedBookingStatus"
                            :options="bookingStatuses"
                            :loading="loadingBookingStatuses"
                            :disabled="
                              loadingBookingStatuses || updatingBookingStatus
                            "
                            track-by="id"
                            label="name"
                            placeholder="Select status"
                            class="booking-status-select"
                            @input="onBookingStatusChange"
                          >
                            <template
                              slot="singleLabel"
                              slot-scope="{ option }"
                            >
                              {{ option.name }}
                            </template>
                          </Multiselect>
                          <p
                            v-if="hasManualBookingStatus"
                            class="booking-status-manual-hint"
                          >
                            Status was set manually. Automatic updates are
                            paused.
                          </p>
                          <button
                            v-if="hasManualBookingStatus"
                            type="button"
                            class="booking-status-resume-btn"
                            :disabled="resumingAutoStatus"
                            @click="resumeAutoBookingStatus"
                          >
                            {{
                              resumingAutoStatus
                                ? 'Resuming…'
                                : 'Resume auto status'
                            }}
                          </button>
                        </dd>
                      </div>
                      <div class="detail-list__row">
                        <dt>Created</dt>
                        <dd>{{ ReservationDetails.date | formatDate }}</dd>
                      </div>
                      <div
                        v-if="ReservationDetails.checked_in_at"
                        class="detail-list__row"
                      >
                        <dt>Checked in</dt>
                        <dd>
                          {{ ReservationDetails.checked_in_at | formatDate }}
                        </dd>
                      </div>
                      <div
                        v-if="ReservationDetails.checked_out_at"
                        class="detail-list__row"
                      >
                        <dt>Checked out</dt>
                        <dd>
                          {{ ReservationDetails.checked_out_at | formatDate }}
                        </dd>
                      </div>
                      <div class="detail-list__row">
                        <dt>Created by</dt>
                        <dd>
                          {{
                            ReservationDetails.user
                              ? ReservationDetails.user.first_name
                              : '—'
                          }}
                        </dd>
                      </div>
                      <div class="detail-list__row">
                        <dt>Vehicle</dt>
                        <dd>
                          {{
                            ReservationDetails.vehicle_number
                              ? ReservationDetails.vehicle_number
                              : '—'
                          }}
                        </dd>
                      </div>
                      <div class="detail-list__row">
                        <dt>Note</dt>
                        <dd>
                          {{
                            ReservationDetails.note
                              ? ReservationDetails.note
                              : '—'
                          }}
                        </dd>
                      </div>
                    </dl>
                  </div>
                </div>

                <div class="col-lg-4">
                  <div
                    v-if="ReservationDetails && ReservationDetails.guest"
                    class="summary-card guest-details-card"
                  >
                    <div class="summary-card__head">
                      <h6 class="summary-card__title">Guest</h6>
                      <button
                        v-if="!editingGuest"
                        type="button"
                        class="guest-details-card__edit-btn"
                        @click="startEditGuest"
                      >
                        Edit
                      </button>
                    </div>

                    <dl v-if="!editingGuest" class="detail-list">
                      <div class="detail-list__row">
                        <dt>Name</dt>
                        <dd>{{ displayGuestField('name') }}</dd>
                      </div>
                      <div class="detail-list__row">
                        <dt>NIC</dt>
                        <dd>{{ displayGuestField('nic_number') }}</dd>
                      </div>
                      <div class="detail-list__row">
                        <dt>Passport</dt>
                        <dd>{{ displayGuestField('passport_number') }}</dd>
                      </div>
                      <div class="detail-list__row">
                        <dt>Address</dt>
                        <dd>{{ displayGuestField('address') }}</dd>
                      </div>
                      <div class="detail-list__row">
                        <dt>Phone</dt>
                        <dd>{{ displayGuestField('phone') }}</dd>
                      </div>
                      <div class="detail-list__row">
                        <dt>Email</dt>
                        <dd class="detail-list__wrap">
                          {{ displayGuestField('email') }}
                        </dd>
                      </div>
                      <div class="detail-list__row">
                        <dt>Company</dt>
                        <dd>{{ displayGuestField('company') }}</dd>
                      </div>
                      <div class="detail-list__row">
                        <dt>Date of birth</dt>
                        <dd>
                          {{
                            formatGuestDob(
                              ReservationDetails.guest.date_of_birth,
                            )
                          }}
                        </dd>
                      </div>
                    </dl>

                    <form
                      v-else
                      class="guest-details-card__form"
                      @submit.prevent="saveGuestDetails"
                    >
                      <div class="guest-details-card__field">
                        <label>Name *</label>
                        <input
                          v-model="guestForm.name"
                          class="form-control guest-details-card__input"
                          type="text"
                          required
                        />
                      </div>
                      <div class="guest-details-card__field">
                        <label>Phone *</label>
                        <input
                          v-model="guestForm.phone"
                          class="form-control guest-details-card__input"
                          type="tel"
                          required
                        />
                      </div>
                      <div class="guest-details-card__field">
                        <label>Email</label>
                        <input
                          v-model="guestForm.email"
                          class="form-control guest-details-card__input"
                          type="email"
                        />
                      </div>
                      <div class="guest-details-card__field">
                        <label>Address</label>
                        <input
                          v-model="guestForm.address"
                          class="form-control guest-details-card__input"
                          type="text"
                        />
                      </div>
                      <div class="guest-details-card__field">
                        <label>NIC</label>
                        <input
                          v-model="guestForm.nic_number"
                          class="form-control guest-details-card__input"
                          type="text"
                        />
                      </div>
                      <div class="guest-details-card__field">
                        <label>Passport</label>
                        <input
                          v-model="guestForm.passport_number"
                          class="form-control guest-details-card__input"
                          type="text"
                        />
                      </div>
                      <div class="guest-details-card__field">
                        <label>Company</label>
                        <input
                          v-model="guestForm.company"
                          class="form-control guest-details-card__input"
                          type="text"
                        />
                      </div>
                      <div class="guest-details-card__field">
                        <label>Date of birth</label>
                        <DatePicker
                          v-model="guestForm.date_of_birth"
                          placeholder="Date of birth"
                          :validate="false"
                          :max-date="maxDobDate"
                          min-date="1920-01-01"
                          format="DD/MM/YYYY"
                        />
                      </div>
                      <div class="guest-details-card__actions">
                        <button
                          type="button"
                          class="guest-details-card__cancel-btn"
                          :disabled="savingGuest"
                          @click="cancelEditGuest"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          class="guest-details-card__save-btn"
                          :disabled="savingGuest"
                        >
                          {{ savingGuest ? 'Saving…' : 'Save' }}
                        </button>
                      </div>
                      <p class="guest-details-card__hint">
                        Saves to guest and admin customer records.
                      </p>
                    </form>
                  </div>
                </div>

                <div class="col-lg-4">
                  <div class="summary-card">
                    <h6 class="summary-card__title">Payment summary</h6>
                    <dl class="detail-list">
                      <div class="detail-list__row">
                        <dt>Room total</dt>
                        <dd class="detail-list__money">
                          {{
                            formatMoney(ReservationDetails.room_total_amount)
                          }}
                        </dd>
                      </div>
                      <div class="detail-list__row">
                        <dt>Order total</dt>
                        <dd class="detail-list__money">
                          {{
                            formatMoney(ReservationDetails.order_total_amount)
                          }}
                        </dd>
                      </div>
                      <div class="detail-list__row">
                        <dt>Grand total</dt>
                        <dd
                          class="detail-list__money detail-list__money--strong"
                        >
                          {{ formatMoney(ReservationDetails.total_amount) }}
                        </dd>
                      </div>
                      <div class="detail-list__row">
                        <dt>Amount paid</dt>
                        <dd class="detail-list__money">
                          {{ formatMoney(ReservationDetails.paid_amount) }}
                        </dd>
                      </div>
                      <div class="detail-list__row">
                        <dt>Balance due</dt>
                        <dd class="detail-list__money detail-list__money--due">
                          {{
                            formatMoney(
                              ReservationDetails.net_amount -
                                ReservationDetails.paid_amount,
                            )
                          }}
                        </dd>
                      </div>
                      <div class="detail-list__row">
                        <dt>Discount</dt>
                        <dd class="detail-list__money">
                          {{ formatMoney(ReservationDetails.discount || 0) }}
                        </dd>
                      </div>
                    </dl>
                  </div>
                </div>
              </div>

              <div class="layout">
                <input
                  name="nav"
                  type="radio"
                  class="nav Guest-radio"
                  id="Guest"
                  checked="checked"
                />
                <div class="page home-page rd-section--rooms">
                  <div class="page-contents">
                    <h6 class="rd-section-title">Room types</h6>
                    <div class="row">
                      <div class="col-md-12">
                        <div class="row">
                          <div class="col-lg-10"></div>
                          <div class="col-lg-2 buttonpay">
                            <button
                              type="button"
                              @click="addOneMoreRoom()"
                              class="addroom"
                            >
                              Add Room
                            </button>
                          </div>
                        </div>
                        <!-- Recent Orders -->
                        <div class="card">
                          <div class="card-body">
                            <div class="table-responsive">
                              <table
                                class="datatable table table-hover table-center mb-0"
                              >
                                <thead>
                                  <tr>
                                    <th>Room Name</th>
                                    <th>Check In</th>
                                    <th>Check Out</th>

                                    <th>Adults</th>
                                    <th>Child</th>

                                    <th>Room Type</th>
                                    <th>Amenities</th>
                                    <th>Amount</th>
                                    <th>No Of Days</th>
                                    <th>Total Amount</th>
                                    <th class="row-actions">Actions</th>
                                  </tr>
                                </thead>
                                <!-- {{order.items}} -->

                                <tbody>
                                  <tr
                                    v-for="(item,
                                    index) in ReservationDetails.rooms"
                                  >
                                    <td>
                                      {{ item.room.name }}
                                    </td>
                                    <td>
                                      {{ item.check_in | formatDate }}
                                      <!-- {{moment(Date.parse(item.check_in)).format('DD/MM/YYYY')  }} -->
                                    </td>
                                    <td>
                                      {{ item.check_out | formatDate }}
                                    </td>

                                    <td>
                                      {{ item.adults }}
                                    </td>
                                    <td>
                                      {{ item.child }}
                                    </td>
                                    <td>
                                      {{ item.roomType.name }}
                                    </td>
                                    <td class="rd-col-wrap">
                                      <div
                                        class="col-lg-12 headers"
                                        v-for="item in item.amenities"
                                      >
                                        {{ item.name }}
                                      </div>
                                    </td>
                                    <td v-if="!editpaymentTextBox">
                                      {{ item.amount }}
                                    </td>
                                    <td v-if="editpaymentTextBox">
                                      <input
                                        class="form-control"
                                        placeholder="Amount"
                                        name="Amount"
                                        v-model="item.amount"
                                        min="0"
                                        type="number"
                                        oninput="validity.valid||(value='');"
                                      />
                                      <label
                                        style="color: red"
                                        v-if="item.amount < 0"
                                        >Amount is not Valid</label
                                      >
                                    </td>
                                    <td>
                                      {{ item.duration }}
                                    </td>
                                    <td>
                                      {{ item.amount * item.duration }}
                                    </td>
                                    <td class="row-actions">
                                      <a
                                        class="ButtonEdit1"
                                        v-on:click.stop="
                                          EditRoom(
                                            item.id,
                                            'modal-room-edit' + item.id,
                                          )
                                        "
                                      >
                                        <i class="icon_pencil-edit"></i>
                                      </a>

                                      <a
                                        class="ButtonEdit"
                                        v-on:click.stop="
                                          ViewSingleRoom(
                                            item.id,
                                            'modal-room-' + item.id,
                                          )
                                        "
                                      >
                                        <i
                                          class="fa fa-eye"
                                          aria-hidden="true"
                                        ></i>
                                      </a>
                                      <a
                                        class="ButtonEdit2"
                                        v-on:click.stop="
                                          DeletesingleRoom(
                                            item.id,
                                            'modal-room-delete-' + item.id,
                                          )
                                        "
                                      >
                                        <i class="icon_trash_alt"></i>
                                      </a>
                                      <a
                                        v-if="editpaymentTextBox && item.amount > 0"
                                        class="UpdateButton"
                                        @click.stop="UpdateAmountRoom(item)"
                                      >
                                        <i class="icon_check_alt2"></i>
                                      </a>
                                      <button
                                        v-if="canAddRoomOrder"
                                        type="button"
                                        class="food-order-btn"
                                        @click.stop="addFoodReservation(item)"
                                      >
                                        <i class="icon_mug" aria-hidden="true"></i>
                                        Add order
                                      </button>
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                        <!-- /Recent Orders -->
                      </div>
                    </div>
                  </div>
                </div>

                <label class="nav" for="Guest">
                  <span> Rooms </span>
                </label>

                <input
                  name="nav"
                  type="radio"
                  class="Orders-radio"
                  id="Orders"
                />
                <div class="page home-page rd-section--orders">
                  <div class="page-contents">
                    <h6 class="rd-section-title">Orders</h6>
                    <div class="row">
                      <div class="col-md-12">
                        <div
                          v-if="canAddRoomOrder"
                          class="reservation-tab-toolbar"
                        >
                          <div
                            v-if="reservationRooms.length > 1"
                            class="reservation-tab-toolbar__field"
                          >
                            <label
                              class="reservation-tab-toolbar__label"
                              for="room-order-select"
                            >
                              Room
                            </label>
                            <select
                              id="room-order-select"
                              v-model="selectedRoomForOrder"
                              class="reservation-tab-toolbar__select"
                            >
                              <option
                                v-for="roomItem in reservationRooms"
                                :key="roomItem.id"
                                :value="roomItem.room.id"
                              >
                                {{ roomItem.room.name }}
                              </option>
                            </select>
                          </div>
                          <button
                            type="button"
                            class="reservation-tab-toolbar__btn"
                            @click="addRoomOrderFromTab"
                          >
                            Add order
                          </button>
                        </div>
                        <div class="card">
                          <div class="card-body">
                            <div class="table-responsive">
                              <table
                                class="datatable table table-hover table-center mb-0"
                              >
                                <thead>
                                  <tr>
                                    <th>Order number</th>
                                    <th>Date</th>
                                    <th>Total Amount</th>
                                    <th>Room name</th>
                                    <th>Room reference number</th>
                                    <th class="rd-col-actions">Actions</th>
                                  </tr>
                                </thead>

                                <tbody>
                                  <tr
                                    v-if="
                                      !ReservationDetails.orders ||
                                        !ReservationDetails.orders.length
                                    "
                                  >
                                    <td
                                      colspan="6"
                                      class="reservation-empty-row"
                                    >
                                      No data available
                                    </td>
                                  </tr>
                                  <tr
                                    v-for="(order,
                                    index) in ReservationDetails.orders"
                                    :key="order.id || index"
                                  >
                                    <td>{{ order.order_number }}</td>
                                    <td>{{ order.date | formatDate }}</td>
                                    <td>{{ order.total_amount }}</td>
                                    <td>
                                      {{
                                        order.room && order.room.name
                                          ? order.room.name
                                          : '—'
                                      }}
                                    </td>
                                    <td>
                                      {{
                                        order.room &&
                                        order.room.reference_number
                                          ? order.room.reference_number
                                          : '—'
                                      }}
                                    </td>
                                    <td class="row-actions">
                                      <a
                                        class="ButtonEdit"
                                        @click="
                                          openToSelectOrder(
                                            index,
                                            'modal-order-' + order.id,
                                            order,
                                          )
                                        "
                                      >
                                        <i
                                          class="fa fa-eye"
                                          aria-hidden="true"
                                        ></i>
                                      </a>

                                      <a
                                        class="ButtonEdit2"
                                        @click="
                                          DeletesFoodOrder(
                                            order.id,
                                            'modal-food-' + order.id,
                                          )
                                        "
                                      >
                                        <i class="icon_trash_alt"></i>
                                      </a>
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                        <!-- /Recent Orders -->
                      </div>
                    </div>
                  </div>
                </div>

                <label class="nav" for="Orders">
                  <span> Orders </span>
                </label>

                <input
                  name="nav"
                  type="radio"
                  class="Payments-radio"
                  id="Payments"
                />
                <div class="page contact-page rd-section--payments">
                  <div class="page-contents">
                    <h6 class="rd-section-title">Payments</h6>
                    <div class="rd-pay-head">
                      <div class="rd-pay-summary">
                        <span class="rd-pay-summary__label">Total Amount</span>
                        <span class="rd-pay-summary__value">
                          {{
                            ReservationDetails.total_amount | formatCurrency
                          }}.00
                        </span>
                        <span class="rd-pay-summary__label"
                          >Pending Payment</span
                        >
                        <span class="rd-pay-summary__value">
                          {{
                            (ReservationDetails.net_amount -
                              ReservationDetails.paid_amount)
                              | formatCurrency
                          }}.00
                        </span>
                        <template v-if="!discountAdd">
                          <span class="rd-pay-summary__label">Discount</span>
                          <span class="rd-pay-summary__value">
                            {{
                              (ReservationDetails.discount
                                ? ReservationDetails.discount
                                : 0) | formatCurrency
                            }}.00
                          </span>
                        </template>
                      </div>
                      <div class="rd-pay-actions">
                        <button
                          class="discountButton"
                          @click="
                            addDiscountReservation(
                              ReservationDetails.id,
                              'modal-discount-' + ReservationDetails.id,
                            )
                          "
                        >
                          {{
                            ReservationDetails.discount > 0
                              ? 'Edit Discount'
                              : 'Add Discount'
                          }}
                        </button>
                        <button
                          v-if="
                            ReservationDetails.net_amount -
                              ReservationDetails.paid_amount >
                              0
                          "
                          @click="
                            makePayment(
                              'modal-payment-' + ReservationDetails.id,
                            )
                          "
                          class="Pay"
                        >
                          Pay
                        </button>
                      </div>
                    </div>
                    <div class="table-responsive">
                      <table
                        class="datatable table table-hover table-center mb-0"
                      >
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Reference Number</th>
                            <th>Payment Type</th>
                            <th>Amount</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-if="
                              !ReservationDetails.payments ||
                                !ReservationDetails.payments.length
                            "
                          >
                            <td colspan="5" class="reservation-empty-row">
                              No data available
                            </td>
                          </tr>
                          <tr
                            v-for="(payment,
                            index) in ReservationDetails.payments"
                            :key="payment.id || index"
                          >
                            <td>{{ payment.date_time | formatDate }}</td>
                            <td>
                              {{
                                payment.reference_number
                                  ? payment.reference_number
                                  : '-'
                              }}
                            </td>
                            <td>{{ payment.payment_type }}</td>
                            <td>{{ payment.amount | formatCurrency }}</td>
                            <td>{{ payment.status }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
                <label class="nav" for="Payments">
                  <span> Payments </span>
                </label>
              </div>

              <div
                v-if="ReservationDetails.status === 'Reserved'"
                class="reservation-details-page__actions"
              >
                <a
                  v-if="ReservationDetails.status === 'Reserved'"
                  class="reservation-details-page__btn reservation-details-page__btn--cancel"
                  @click="
                    cancelBooking('modal-booking-' + ReservationDetails.id)
                  "
                >
                  <i class="icon_blocked" aria-hidden="true"></i>
                  Cancel Booking
                </a>
              </div>

              <section class="rd-history">
                <h6 class="rd-section-title">History</h6>
                <div class="table-responsive">
                  <table class="datatable table table-hover table-center mb-0">
                    <thead>
                      <tr>
                        <th>Date and time</th>
                        <th>Change</th>
                        <th>Details</th>
                        <th>From</th>
                        <th>To</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-if="historyLoading">
                        <td colspan="5" class="reservation-empty-row">
                          Loading history…
                        </td>
                      </tr>
                      <tr v-else-if="!history.length">
                        <td colspan="5" class="reservation-empty-row">
                          No data available
                        </td>
                      </tr>
                      <tr v-for="item in pagedHistory" :key="item.id">
                        <td>{{ formatWhen(item.created_at) }}</td>
                        <td>{{ item.field_label }}</td>
                        <td class="rd-col-wrap">{{ item.summary }}</td>
                        <td>{{ item.old_value || '—' }}</td>
                        <td>{{ item.new_value || '—' }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div
                  v-if="!historyLoading && history.length"
                  class="rd-history__pager"
                >
                  <span class="rd-history__count">
                    {{ historyFrom }}–{{ historyTo }} of {{ history.length }}
                  </span>
                  <div class="rd-history__pages">
                    <button
                      type="button"
                      class="rd-history__page"
                      :disabled="historyPage <= 1"
                      @click="setHistoryPage(historyPage - 1)"
                    >
                      Previous
                    </button>
                    <button
                      v-for="pageNum in historyPageNumbers"
                      :key="'history-page-' + pageNum"
                      type="button"
                      class="rd-history__page rd-history__page--num"
                      :class="{ 'rd-history__page--active': pageNum === historyPage }"
                      @click="setHistoryPage(pageNum)"
                    >
                      {{ pageNum }}
                    </button>
                    <button
                      type="button"
                      class="rd-history__page"
                      :disabled="historyPage >= historyLastPage"
                      @click="setHistoryPage(historyPage + 1)"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </section>
            </div>
          </div>
          <!-- /box_booking -->
        </div>
        <!-- /col -->
      </div>
      <!-- /row -->

      <div>
        <CModal
          Text="customize"
          ref="FoodModal"
          headerText="Foods"
          :width="850"
          :hideButton="true"
          :visibleFooter="false"
          :modalName="'modal-order-' + foodVisibleId"
        >
          <template v-slot:content>
            <div class="row">
              <div class="col-md-12">
                <!-- Recent Orders -->
                <div class="card">
                  <div class="card-body">
                    <div class="table-responsive">
                      <table
                        class="datatable table table-hover table-center mb-0"
                      >
                        <thead>
                          <tr>
                            <th>Dish</th>
                            <th>Quantity</th>
                            <th>Unit</th>
                            <th>Amount</th>
                          </tr>
                        </thead>
                        <!-- {{order.items}} -->

                        <tbody v-for="(itemfood, index) in foodDataStorage">
                          <tr>
                            <td>
                              {{ itemfood.dish.name }}
                            </td>
                            <td>
                              {{ itemfood.quantity }}
                            </td>
                            <td>
                              {{ itemfood.unit.name }}
                            </td>
                            <td>
                              {{ itemfood.amount * itemfood.quantity }}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
                <!-- /Recent Orders -->
              </div>
            </div>
          </template>
        </CModal>
      </div>

      <CModal
        Text="customize"
        ref="discount"
        headerText="Discount"
        submitText="Add"
        :hideButton="true"
        @onClickSubmit="makeDiscountDetails()"
        :visibleFooter="true"
        :width="800"
        :modalName="'modal-discount-' + ReservationDetails.id"
      >
        <template v-slot:content>
          <div class="row">
            <div class="col-lg-12">
              <div class="container">
                <div class="row">
                  <div class="col-lg-3"></div>
                  <div class="col-lg-3"><b>Discount Amount</b></div>
                  <div class="col-lg-3 mb">
                    <input
                      class="form-control"
                      placeholder="Amount"
                      v-model="discount.amount"
                      min="0"
                      type="number"
                      oninput="validity.valid||(value='');"
                    />

                    <label style="color: red" v-if="amount < 0">
                      Please Enter Amount Greater Than 0</label
                    >
                  </div>
                  <div class="col-lg-3"></div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </CModal>
      <CModal
        Text="customize"
        ref="Payment"
        headerText="Payments"
        submitText="Pay"
        :hideButton="true"
        @onClickSubmit="makePaymentDetails()"
        :visibleFooter="true"
        :width="950"
        :modalName="'modal-payment-' + ReservationDetails.id"
      >
        <template v-slot:content>
          <div class="row">
            <div class="col-lg-12">
              <div class="container-fluid">
                <div class="row">
                  <div class="col-lg-3 text-lg-left" style="font-weight: bold">
                    Total Payable Amount
                  </div>
                  <div
                    class="col-lg-3 text-lg-center"
                    style="color: #c11f66; font-weight: bold"
                  >
                    {{
                      ReservationDetails.net_amount -
                        ReservationDetails.paid_amount
                    }}.00
                  </div>

                  <div class="col-lg-3" style="font-weight: bold">Amount</div>
                  <div class="col-lg-3">
                    <input
                      class="form-control text-lg-center"
                      placeholder="Amount"
                      v-model="Payment.amount"
                      min="0"
                      type="number"
                      oninput="validity.valid||(value='');"
                    />

                    <label
                      style="color: red"
                      v-if="
                        Payment.amount >
                          ReservationDetails.net_amount -
                            ReservationDetails.paid_amount
                      "
                    >
                      Please Enter Amount Less Than Payable Amount</label
                    >
                  </div>
                </div>
                <div class="row" style="margin-top: 15px"></div>

                <div class="row">
                  <div class="col-lg-3 text-lg-left" style="font-weight: bold">
                    Payment Type
                  </div>
                  <div class="col-lg-3">
                    <Multiselect
                      v-model="Payment.payment_type"
                      selectLabel=""
                      :options="PaymentTypes"
                      placeholder="Payment Type"
                      track-by="name"
                      label="name"
                      ><span slot="noResult"
                        >Oops! No elements found. Consider changing the search
                        query.</span
                      >
                      <template slot="singleLabel" slot-scope="{ option }">
                        {{ option.name }}
                      </template>
                    </Multiselect>
                  </div>

                  <div class="col-lg-3" style="font-weight: bold">Note</div>

                  <div class="col-lg-3">
                    <input
                      class="form-control paragraph text-lg-center"
                      placeholder="Note"
                      v-model="Payment.remarks"
                    />
                  </div>
                </div>

                <div class="row" style="margin-top: 5px"></div>

                <div class="row">
                  <div
                    class="col-lg-3 text-left"
                    v-if="Payment.payment_type.name === 'Card'"
                  >
                    <b> Bank</b>
                  </div>
                  <div
                    class="col-lg-3"
                    v-if="Payment.payment_type.name === 'Card'"
                  >
                    <input
                      class="form-control"
                      placeholder="Bank"
                      v-model="Payment.card_bank"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </CModal>

      <CModal
        Text="customize"
        ref="singleRoomedit"
        headerText="Edit Room Booking"
        submitText="Confirm"
        :hideButton="true"
        @onClickSubmit="makeBookingCancel()"
        :visibleFooter="false"
        :width="1000"
        :modalName="'modal-room-edit' + editroom"
      >
        <template v-slot:content>
          <div>
            <!-- {{SingleRoomDetails}} -->
          </div>
          <SingleRoom
            :item="SingleRoomDetails"
            :updateRoom="true"
            :Filter="dateRange"
            :defaultroom_type="SingleRoomDetails.roomType"
            :ReservationId="ReservationDetails.id"
            @Afterhide="closeRoomPopup()"
          />
        </template>
      </CModal>

      <CModal
        Text="customize"
        ref="singleRoomCancel"
        headerText="Room Cancel"
        submitText="Confirm"
        :hideButton="true"
        @onClickSubmit="makeBookingCancel()"
        :visibleFooter="false"
        :width="400"
        :height="200"
        :modalName="'modal-room-delete-' + CancelRoom"
      >
        <template v-slot:content>
          <h5>Are you sure cancel room?</h5>
          <button class="booknow" @click="CancelRoomDetails">
            Cancel Room
          </button>
          <!-- <div class="container margin_60_40 vh-100">
                 
          </div> -->
        </template>
      </CModal>
      <CModal
        Text="customize"
        ref="singleRoom"
        headerText="Room"
        submitText="Confirm"
        :hideButton="true"
        @onClickSubmit="makeBookingCancel()"
        :visibleFooter="false"
        :width="900"
        :modalName="'modal-room-' + Viewroom"
      >
        <template v-slot:content>
          <div>
            <!-- {{SingleRoomDetails}} -->
          </div>
          <SingleRoom :item="SingleRoomDetails" :ViewRoom="true" />
        </template>
      </CModal>
      <CModal
        Text="customize"
        ref="foodorder"
        headerText="Cancel Order"
        submitText="Confirm"
        :hideButton="true"
        @onClickSubmit="makeFoodCancel()"
        :visibleFooter="true"
        :width="550"
        :modalName="'modal-food-' + foodId"
      >
        <template v-slot:content>
          <div class="row">
            <div class="col-lg-12">
              <div class="box_booking_2"></div>
              <div class="container margin_60_40">
                <div class="row">
                  <div class="col-lg-12">
                    <input
                      class="form-control"
                      placeholder="Reason"
                      v-model="FoodOrdercancel.note"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </CModal>
      <CModal
        Text="customize"
        ref="booking"
        headerText="Cancel Booking"
        submitText="Confirm"
        :hideButton="true"
        @onClickSubmit="makeBookingCancel()"
        :visibleFooter="true"
        :width="550"
        :modalName="'modal-booking-' + ReservationDetails.id"
      >
        <template v-slot:content>
          <div class="row">
            <div class="col-lg-12">
              <div class="box_booking_2"></div>
              <div class="container margin_60_40">
                <div class="row">
                  <div class="col-lg-12">
                    <input
                      class="form-control"
                      placeholder="Reason"
                      v-model="BookingCancel.note"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </CModal>
    </main>
  </div>
</template>

<script>
import SingleRoom from './SingleRoom';
import Courosel from '@/components/base/Caurosel/index';
import Multiselect from 'vue-multiselect';
import { Header } from '@/components/index.js';

import ReservationBill from '../components/Share/ReservationBill/index.vue';
import { CustomModal as CModal } from '@/components/base/index.js';

import { Loader } from '@/components/base/index.js';
const iconComponent = {
  template: `
        <span class="my-icon">{{ opened ? 'I am open' : ' I am closed' }}</span>
    `,
  props: {
    opened: {
      type: Boolean,
      default: false,
    },
  },
};

import VueRangeSlider from 'vue-range-component';
import 'vue-range-component/dist/vue-range-slider.css';
import axios from '@/plugins/axios';
import moment from 'moment';
import { DatePicker } from '@/components/base/index';
import { normalizePickerDate } from '@/utils/datehelper';

export default {
  name: 'Home',
  data() {
    return {
      iconComponent: iconComponent,
      ReservationDetails: [],
      foodDataStorage: [],
      foodVisibleId: null,
      foodId: null,
      coverImagePreview: [],
      dateRange: { start: '', end: '' },
      SingleRoomDetails: [],
      isSuspend: false,
      discount: { amount: 0 },

      CheckIn: '',
      BookingCancel: {
        note: '',
        status: 'Cancelled',
      },
      FoodOrdercancel: {
        note: '',
        status: 'cancelled',
      },
      completeBill: false,
      CheckOut: '',
      RegisteredDate: '',
      paymentAmount: 0,
      Viewroom: null,
      CancelRoom: null,
      editroom: null,
      Payment: {
        payment_type: '',
        amount: null,
        date_time: moment().format('YYYY-MM-DD'),
        remarks: '',
      },

      PaymentTypes: [
        {
          name: 'Card',
        },
        {
          name: 'Cash',
        },
      ],
      note: null,
      loginSuccess: false,
      discountAdd: false,
      editpaymentTextBox: false,
      completeBill: false,
      loading: false,
      paymentType: null,
      bank: null,
      bookingStatuses: [],
      selectedBookingStatus: null,
      loadingBookingStatuses: false,
      updatingBookingStatus: false,
      checkingIn: false,
      checkingOut: false,
      resumingAutoStatus: false,
      editingGuest: false,
      history: [],
      historyLoading: false,
      historyPage: 1,
      historyPageSize: 10,
      savingGuest: false,
      selectedRoomForOrder: null,
      guestForm: {
        name: '',
        phone: '',
        email: '',
        address: '',
        nic_number: '',
        passport_number: '',
        company: '',
        date_of_birth: null,
      },
    };
  },
  components: {
    Header,
    Loader,
    CModal,
    Courosel,
    Multiselect,
    ReservationBill,
    SingleRoom,
    DatePicker,
  },
  computed: {
    historyLastPage() {
      return Math.max(1, Math.ceil(this.history.length / this.historyPageSize));
    },
    pagedHistory() {
      const start = (this.historyPage - 1) * this.historyPageSize;
      return this.history.slice(start, start + this.historyPageSize);
    },
    historyFrom() {
      if (!this.history.length) return 0;
      return (this.historyPage - 1) * this.historyPageSize + 1;
    },
    historyTo() {
      return Math.min(this.historyPage * this.historyPageSize, this.history.length);
    },
    historyPageNumbers() {
      const total = this.historyLastPage;
      const current = this.historyPage;
      const maxVisible = 5;
      if (total <= maxVisible) {
        return Array.from({ length: total }, (_, index) => index + 1);
      }
      let start = Math.max(1, current - 2);
      let end = Math.min(total, start + maxVisible - 1);
      start = Math.max(1, end - maxVisible + 1);
      return Array.from({ length: end - start + 1 }, (_, index) => start + index);
    },
    paymentStatusClass() {
      const status = this.ReservationDetails && this.ReservationDetails.status;
      return {
        'payment-status-badge--reserved': status === 'Reserved',
        'payment-status-badge--paid': status === 'Paid',
        'payment-status-badge--partial': status === 'PartiallyPaid',
        'payment-status-badge--cancelled': status === 'Cancelled',
      };
    },
    canCheckIn() {
      const details = this.ReservationDetails;
      return (
        details &&
        details.id &&
        details.status !== 'Cancelled' &&
        !details.checked_in_at
      );
    },
    canCheckOut() {
      const details = this.ReservationDetails;
      return (
        details &&
        details.id &&
        details.status !== 'Cancelled' &&
        details.checked_in_at &&
        !details.checked_out_at
      );
    },
    hasManualBookingStatus() {
      const details = this.ReservationDetails;
      return details && details.booking_status_manual === true;
    },
    reservationRooms() {
      const rooms = this.ReservationDetails && this.ReservationDetails.rooms;
      return Array.isArray(rooms) ? rooms : [];
    },
    canAddRoomOrder() {
      const details = this.ReservationDetails;
      if (!details || !this.reservationRooms.length) return false;
      return details.status !== 'Paid' && details.status !== 'Cancelled';
    },
    maxDobDate() {
      return moment().format('YYYY-MM-DD');
    },
  },
  methods: {
    goBack() {
      if (window.history.length > 1) {
        this.$router.back();
      } else {
        this.$router.push('/all-reserved-rooms');
      }
    },
    formatPaymentStatus(status) {
      if (!status) return '—';
      if (status === 'PartiallyPaid') return 'Partially Paid';
      return status;
    },
    formatMoney(amount) {
      const n = parseFloat(amount);
      if (!Number.isFinite(n)) return '0.00';
      return n.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    displayGuestField(field) {
      const guest = this.ReservationDetails && this.ReservationDetails.guest;
      if (
        !guest ||
        guest[field] === null ||
        guest[field] === undefined ||
        guest[field] === '' ||
        guest[field] === 0 ||
        guest[field] === '0'
      ) {
        return '—';
      }
      return guest[field];
    },
    formatGuestDob(value) {
      if (!value || value === 0 || value === '0') return '—';
      const parsed = moment(value);
      return parsed.isValid() ? parsed.format('DD/MM/YYYY') : '—';
    },

    startEditGuest() {
      const g = this.ReservationDetails.guest;
      this.guestForm = {
        name: g.name || '',
        phone: g.phone || '',
        email: g.email || '',
        address: g.address || '',
        nic_number: g.nic_number || '',
        passport_number: g.passport_number || '',
        company: g.company || '',
        date_of_birth: normalizePickerDate(g.date_of_birth),
      };
      this.editingGuest = true;
    },

    cancelEditGuest() {
      this.editingGuest = false;
    },

    buildGuestUpdatePayload() {
      const email = (this.guestForm.email || '').trim();
      return {
        name: (this.guestForm.name || '').trim(),
        phone: (this.guestForm.phone || '').trim(),
        email: email || null,
        address: (this.guestForm.address || '').trim() || null,
        nic_number: (this.guestForm.nic_number || '').trim() || null,
        passport_number: (this.guestForm.passport_number || '').trim() || null,
        company: (this.guestForm.company || '').trim() || null,
        date_of_birth: (() => {
          const dob = normalizePickerDate(this.guestForm.date_of_birth);
          return dob ? moment(dob).format('YYYY-MM-DD') : null;
        })(),
      };
    },

    extractApiErrorMessage(err, fallback) {
      const data = err && err.response && err.response.data;
      if (!data) {
        return (err && err.message) || fallback;
      }
      if (typeof data === 'string') {
        return data || fallback;
      }
      if (data.message) {
        return data.message;
      }
      if (data.error && typeof data.error === 'object') {
        const first = Object.values(data.error).find((v) => v);
        if (first) return String(first);
      }
      return fallback;
    },

    async patchGuestRecord(guestId, payload) {
      const urls = [`hms/web/guests/${guestId}`, `hms/guests/${guestId}`];
      let lastError = null;
      for (const url of urls) {
        try {
          return await axios.patch(url, payload);
        } catch (err) {
          lastError = err;
          const status = err.response && err.response.status;
          if (status !== 404 && status !== 405) {
            throw err;
          }
        }
      }
      throw lastError;
    },

    async saveGuestDetails() {
      const guestId = this.ReservationDetails.guest.id;
      const payload = this.buildGuestUpdatePayload();
      if (!guestId || !payload.name || !payload.phone) {
        this.$fire({
          title: 'Error',
          text: 'Name and phone are required.',
          type: 'error',
        });
        return;
      }
      if (payload.name.length < 3) {
        this.$fire({
          title: 'Error',
          text: 'Name must be at least 3 characters.',
          type: 'error',
        });
        return;
      }

      this.savingGuest = true;
      try {
        await this.patchGuestRecord(guestId, payload);
        this.editingGuest = false;
        await this.GetReservationDetails();
        this.$fire({
          title: 'Saved',
          text: 'Guest and admin customer updated.',
          type: 'success',
        });
      } catch (err) {
        const msg = this.extractApiErrorMessage(
          err,
          'Could not save guest details. Restart the API server if you recently updated the backend.',
        );
        this.$fire({ title: 'Error', text: msg, type: 'error' });
      } finally {
        this.savingGuest = false;
      }
    },
    parseBookingStatusList(payload) {
      if (Array.isArray(payload)) {
        return payload;
      }
      if (payload && Array.isArray(payload.data)) {
        return payload.data;
      }
      return [];
    },
    loadBookingStatuses() {
      this.loadingBookingStatuses = true;
      const applyStatuses = (res) => {
        this.bookingStatuses = this.parseBookingStatusList(res.data);
        this.syncSelectedBookingStatus();
      };

      axios
        .get('hms/web/booking-statuses')
        .then(applyStatuses)
        .catch(() =>
          axios
            .get('booking-statuses/search?status=active')
            .then(applyStatuses)
            .catch(() => {
              this.bookingStatuses = [];
            }),
        )
        .finally(() => {
          this.loadingBookingStatuses = false;
        });
    },
    syncSelectedBookingStatus() {
      const status =
        this.ReservationDetails && this.ReservationDetails.booking_status;
      if (!status) {
        this.selectedBookingStatus = null;
        return;
      }
      this.selectedBookingStatus =
        this.bookingStatuses.find((item) => item.id === status.id) || status;
    },
    onBookingStatusChange(status) {
      if (
        !status ||
        !this.ReservationDetails ||
        !this.ReservationDetails.id ||
        status.id === this.ReservationDetails.booking_status_id
      ) {
        return;
      }

      this.updatingBookingStatus = true;
      axios
        .patch(
          `hms/web/reservations/${this.ReservationDetails.id}/booking-status`,
          {
            booking_status_id: status.id,
          },
        )
        .then(() => {
          this.GetReservationDetails();
        })
        .catch(() => {
          this.syncSelectedBookingStatus();
          this.$fire({
            title: 'Error',
            text: 'Could not update booking status.',
            type: 'error',
          });
        })
        .finally(() => {
          this.updatingBookingStatus = false;
        });
    },
    checkInGuest() {
      if (!this.ReservationDetails || !this.ReservationDetails.id) return;
      this.checkingIn = true;
      axios
        .post(`hms/web/reservations/${this.ReservationDetails.id}/check-in`)
        .then(() => {
          this.GetReservationDetails();
          this.$fire({
            title: 'Checked in',
            text: 'Guest checked in and booking status updated.',
            type: 'success',
            timer: 2500,
          });
        })
        .catch((err) => {
          const msg =
            (err.response && err.response.data && err.response.data.message) ||
            'Could not check in guest.';
          this.$fire({ title: 'Error', text: msg, type: 'error' });
        })
        .finally(() => {
          this.checkingIn = false;
        });
    },
    checkOutGuest() {
      if (!this.ReservationDetails || !this.ReservationDetails.id) return;
      this.checkingOut = true;
      axios
        .post(`hms/web/reservations/${this.ReservationDetails.id}/check-out`)
        .then(() => {
          this.GetReservationDetails();
          this.$fire({
            title: 'Checked out',
            text: 'Guest checked out and booking status updated.',
            type: 'success',
            timer: 2500,
          });
        })
        .catch((err) => {
          const msg =
            (err.response && err.response.data && err.response.data.message) ||
            'Could not check out guest.';
          this.$fire({ title: 'Error', text: msg, type: 'error' });
        })
        .finally(() => {
          this.checkingOut = false;
        });
    },
    resumeAutoBookingStatus() {
      if (!this.ReservationDetails || !this.ReservationDetails.id) return;
      this.resumingAutoStatus = true;
      axios
        .post(
          `hms/web/reservations/${this.ReservationDetails.id}/resume-auto-booking-status`,
        )
        .then(() => {
          this.GetReservationDetails();
          this.$fire({
            title: 'Auto status resumed',
            text: 'Booking status will now update automatically again.',
            type: 'success',
            timer: 2500,
          });
        })
        .catch((err) => {
          const msg =
            (err.response && err.response.data && err.response.data.message) ||
            'Could not resume automatic booking status.';
          this.$fire({ title: 'Error', text: msg, type: 'error' });
        })
        .finally(() => {
          this.resumingAutoStatus = false;
        });
    },
    //delete Food Order popup open
    DeletesFoodOrder(index, modalname) {
      this.foodId = index;
      setTimeout(() => {
        this.$refs.foodorder.show(modalname);
      }, 100);
    },
    //Food Order Cancel Method
    makeFoodCancel() {
      let credential = this.FoodOrdercancel;

      axios
        .patch(`hms/web/orders/${this.foodId}/change-status`, credential)
        .then((res) => {
          this.foodId = null;
          this.FoodOrdercancel.note = '';
          this.$refs.foodorder.hide();
          this.GetReservationDetails();
        })
        .catch((err) => {});
    },
    addFoodReservation(item) {
      this.$store.commit(
        'orders/AddGuestDetails',
        this.ReservationDetails.guest,
      );
      this.$router.push({
        path: '/order/room/' + item.room.id,
        query: {
          returnTo: `/reservation-details/${this.$route.params.id}`,
        },
      });
    },
    addRoomOrderFromTab() {
      const rooms = this.reservationRooms;
      if (!rooms.length) return;
      const roomId = this.selectedRoomForOrder || rooms[0].room.id;
      const roomItem =
        rooms.find((entry) => entry.room.id === roomId) || rooms[0];
      this.addFoodReservation(roomItem);
    },
    syncSelectedRoomForOrder() {
      const rooms = this.reservationRooms;
      if (!rooms.length) {
        this.selectedRoomForOrder = null;
        return;
      }
      const currentExists = rooms.some(
        (entry) => entry.room && entry.room.id === this.selectedRoomForOrder,
      );
      if (!currentExists) {
        this.selectedRoomForOrder = rooms[0].room.id;
      }
    },
    //Delete Single Room In Reservations
    DeletesingleRoom(index, modalname) {
      this.CancelRoom = index;
      this.getSingleRoomDetails(index);
      setTimeout(() => {
        this.$refs.singleRoomCancel.show(modalname);
      }, 100);
    },
    //make Discount method
    makeDiscountDetails() {
      let resid = this.$route.params.id;

      axios
        .post(`hms/web/reservations/${resid}/add-discount`, this.discount)
        .then((res) => {
          // this.SingleRoomDetails = res.data;
          this.$fire({
            title: 'Success',
            text: 'Discount Added SuccessFully',
            type: 'success',
            timer: 3000,
          }).then((r) => {});
          this.$refs.discount.hide();
          this.GetReservationDetails();
        })
        .catch((err) => {
          this.$refs.discount.hide();
        });
    },

    // Open Popup Reservation Discount
    addDiscountReservation(index, modalname) {
      this.discount.amount = this.ReservationDetails.discount;
      this.$refs.discount.show(modalname);
    },

    //Edit Single Room PopUp
    EditRoom(index, modalname) {
      this.editroom = index;
      this.getSingleRoomDetails(index);
      setTimeout(() => {
        this.$refs.singleRoomedit.show(modalname);
      }, 100);
    },
    //close Single Room PopUp
    //close Single Room PopUp
    closeRoomPopup() {
      this.$refs.singleRoomedit.hide();
      this.GetReservationDetails();
    },
    //View Single Room PopUp
    ViewSingleRoom(index, modalname) {
      this.Viewroom = index;
      this.getSingleRoomDetails(index);
      setTimeout(() => {
        this.$refs.singleRoom.show(modalname);
      }, 100);
    },
    // Add One More Room In This Reservation
    addOneMoreRoom() {
      let resid = this.$route.params.id;

      this.$store.commit('orders/updateRoomVisible', true);
      this.$store.commit('orders/AddNewRoomReservationId', resid);
      this.$router.push({
        path: '/rooms-booking',
        query: {
          addRooms: '1',
          returnTo: `/reservation-details/${resid}`,
        },
      });
    },

    // Get Single RoomDetails
    getSingleRoomDetails(index) {
      let id = this.$route.params.id;

      axios
        .get(`hms/web/reservations/${id}/rooms/${index}`)
        .then((res) => {
          this.SingleRoomDetails = res.data;

          this.SingleRoomDetails.check_in = moment(
            new Date(this.SingleRoomDetails.check_in),
          ).format('YYYY-MM-DD');
          this.SingleRoomDetails.check_out = moment(
            new Date(this.SingleRoomDetails.check_out),
          ).format('YYYY-MM-DD');

          this.dateRange.start = this.SingleRoomDetails.check_in;
          this.dateRange.end = this.SingleRoomDetails.check_out;
        })
        .catch((err) => {});
    },
    EditPayment() {
      this.editpaymentTextBox = true;
    },
    UpdateAmountRoom(item) {
      this.editpaymentTextBox = true;

      // hms/web/reservations/11/rooms/14/change-reserved-room-amount?amount=1000​​​​​​​
      axios
        .post(
          `hms/web/reservations/${this.ReservationDetails.id}/rooms/${item.id}/change-reserved-room-amount?amount=${item.amount}`,
        )
        .then((res) => {
          this.editpaymentTextBox = false;

          this.GetReservationDetails();
        })
        .catch((err) => {});
    },

    makeBookingCancel() {
      let credential = this.BookingCancel;

      axios
        .post(
          `hms/web/reservations/${this.ReservationDetails.id}/change-status`,
          credential,
        )
        .then((res) => {
          this.$refs.booking.hide();
          this.GetReservationDetails();
        })
        .catch((err) => {});
    },
    makePayment(modalName) {
      this.$refs.Payment.show(modalName);
    },
    CompleteBooking(index) {
      const { href } = this.$router.resolve({
        path: '/reservation-details-bill/' + index,
      });
      const width = 900;
      const height = 720;
      const left = Math.max(0, (window.screen.availWidth - width) / 2);
      const top = Math.max(0, (window.screen.availHeight - height) / 2);
      const popup = window.open(
        href,
        'reservationBill',
        `popup=yes,width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`,
      );
      if (popup) {
        popup.focus();
      }
    },

    makePaymentDetails() {
      if (this.Payment.amount > 0) {
        let credential = this.Payment;
        credential.payment_type = this.Payment.payment_type.name;

        axios
          .post(
            `hms/web/reservations/${this.ReservationDetails.id}/payments`,
            credential,
          )
          .then((res) => {
            this.Payment.amount = 0;
            this.$refs.Payment.hide();
            this.GetReservationDetails();
          })
          .catch((err) => {});
      }
    },

    openToSelectOrder(index, modalName, itemData) {
      this.foodDataStorage = itemData.items;
      this.foodVisibleId = itemData.id;
      setTimeout(() => {
        this.$refs.FoodModal.show(modalName);
      }, 100);
    },

    setDateFormats() {},
    cancelBooking(value) {
      this.$refs.booking.show(value);
    },
    CancelRoomDetails() {
      let credential = this.SingleRoomDetails;
      let reservationId = this.$route.params.id; // Use reservation ID from route
      let roomReservationId = credential.id; // Use room reservation ID

      credential.room_type_id = credential.roomType.id;
      credential.room_id = credential.room.id;
      credential.adults;
      credential.amount;
      credential.child, credential.number_of_room;
      credential.check_in;
      credential.check_out;
      if (credential.amenities) {
        credential.amenities = credential.amenities.map((p) => {
          return p.id;
        });
      } else {
        credential.amenities = [];
      }
      delete credential.id;
      delete credential.room;
      delete credential.roomType;
      axios
        .post(
          `hms/web/reservations/${reservationId}/rooms/${roomReservationId}/cancel`,
          credential,
        )
        .then((res) => {
          let self = this;
          self.$refs.singleRoomCancel.hide();
          this.GetReservationDetails();
        })
        .catch((err) => {});
    },

    GetReservationDetails() {
      let id = this.$route.params.id;
      this.loadHistory(id);
      return axios
        .get(`hms/web/reservations/${id}`)
        .then((res) => {
          this.ReservationDetails = res.data;
          this.syncSelectedBookingStatus();

          if (
            this.ReservationDetails.guest &&
            this.ReservationDetails.guest.image
          ) {
            this.coverImagePreview = [this.ReservationDetails.guest.image];
          }

          this.ReservationDetails.rooms.map((p) => {
            let Details = p;
            var a = moment(new Date(p.check_out));
            var b = moment(new Date(p.check_in));
            Details.duration = a.diff(b, 'days');
          });
          this.syncSelectedRoomForOrder();
        })
        .catch((err) => {});
    },
    loadHistory(id) {
      this.historyLoading = true;
      axios
        .get(`hms/web/reservations/${id}/history`)
        .then((res) => {
          this.history = Array.isArray(res.data) ? res.data : [];
          this.historyPage = 1;
          this.historyLoading = false;
        })
        .catch(() => {
          this.history = [];
          this.historyPage = 1;
          this.historyLoading = false;
        });
    },
    setHistoryPage(page) {
      const next = Number(page);
      if (!Number.isFinite(next)) return;
      this.historyPage = Math.min(Math.max(1, next), this.historyLastPage);
    },
    formatWhen(value) {
      if (!value) return '—';
      const parsed = moment(value);
      return parsed.isValid() ? parsed.format('DD-MM-YYYY HH:mm') : value;
    },
  },
  created() {
    this.loadBookingStatuses();
    this.GetReservationDetails();
  },
};
</script>

<style lang="scss">
.reservation-details-page .booking-status-select.multiselect {
  min-height: 34px;
}

.reservation-details-page .booking-status-select .multiselect__tags {
  min-height: 34px;
  padding: 4px 36px 0 8px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #fff;
}

.reservation-details-page .booking-status-select .multiselect__single,
.reservation-details-page .booking-status-select .multiselect__placeholder {
  font-size: 13px;
  margin-bottom: 0;
}
</style>

<style scoped>
.reservation-details-page {
  min-height: 100vh;
  padding-top: 60px;
  background: #f8fafc;
  text-align: left;
  box-sizing: border-box;
}

.summary-cards-row {
  align-items: stretch;
  margin-bottom: 8px;
}

.summary-cards-row > [class*='col-'] {
  display: flex;
}

.summary-cards-row .summary-card {
  flex: 1;
  width: 100%;
}

.summary-card {
  display: flex;
  flex-direction: column;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
  padding: 16px 18px 10px;
  margin-bottom: 16px;
  min-height: 100%;
  overflow: hidden;
  background: #fff;
}

.summary-card__title {
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
  color: #242c67;
  margin: 0 0 8px;
  padding-bottom: 10px;
  border-bottom: 1px solid #eef2f6;
  text-align: left;
}

.summary-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  border-bottom: 1px solid #eef2f6;
  padding-bottom: 10px;
  margin-bottom: 8px;
}

.summary-card__head .summary-card__title {
  margin: 0;
  padding-bottom: 0;
  border-bottom: 0;
}

.summary-card__head .summary-card__title {
  margin: 0;
}

.guest-details-card__edit-btn,
.guest-details-card__save-btn {
  border: none;
  background: var(--brand-primary);
  color: #fff;
  font-size: 12px;
  padding: 5px 12px;
  border-radius: 6px;
  cursor: pointer;
}

.guest-details-card__cancel-btn {
  border: 1px solid #dce0e6;
  background: #fff;
  color: #334155;
  font-size: 12px;
  padding: 5px 12px;
  border-radius: 6px;
  cursor: pointer;
}

.detail-list {
  margin: 0;
  text-align: left;
}

.detail-list__row {
  display: grid;
  grid-template-columns: 124px minmax(0, 1fr);
  align-items: center;
  justify-items: start;
  gap: 8px 12px;
  min-height: 36px;
  padding: 6px 0;
  border-bottom: 1px solid #f1f5f9;
  font-size: 13px;
  text-align: left;
}

.detail-list__row:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.detail-list__row--field {
  align-items: start;
}

.detail-list dt {
  margin: 0;
  font-weight: 500;
  color: #64748b;
  font-size: 13px;
  line-height: 1.3;
  letter-spacing: 0;
  text-transform: none;
  text-align: left;
  white-space: nowrap;
}

.detail-list dd {
  margin: 0;
  display: block;
  text-align: left !important;
  color: #0f172a;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.45;
  min-width: 0;
  word-break: break-word;
}

.detail-list__ref {
  color: #c53a2a;
  font-weight: 700;
}

.detail-list__money {
  font-weight: 600;
  color: #c53a2a;
  font-variant-numeric: tabular-nums;
}

.detail-list__money--strong {
  font-weight: 700;
}

.detail-list__money--due {
  color: #c53a2a;
  font-weight: 700;
}

.detail-list__wrap {
  word-break: break-word;
  overflow-wrap: anywhere;
}

.detail-list__row--field dd {
  text-align: left;
}

.detail-list__row--field .booking-status-select {
  max-width: 100%;
}

.detail-list__row--field :deep(.multiselect) {
  min-height: 32px;
  font-size: 13px;
}

.detail-list__row--field :deep(.multiselect__tags) {
  min-height: 32px;
  padding: 4px 32px 0 8px;
}

.detail-list__row--field :deep(.multiselect__single) {
  font-size: 13px;
  margin-bottom: 4px;
}

.detail-list dd .payment-status-badge {
  margin-left: 0;
}

.reservation-tab-toolbar {
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 10px 12px;
  margin-bottom: 12px;
}

.reservation-tab-toolbar__field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 180px;
}

.reservation-tab-toolbar__label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #64748b;
}

.reservation-tab-toolbar__select {
  height: 36px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 0 10px;
  font-size: 14px;
  color: #0f172a;
  background: #fff;
}

.reservation-tab-toolbar__btn,
.food-order-btn,
.addroom {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 36px;
  padding: 8px 16px;
  border-radius: 8px;
  border: 1px solid var(--brand-primary-dark, #3d1f6b);
  background: var(--brand-primary);
  color: #fff !important;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.2;
  cursor: pointer;
  box-shadow: none;
}

.reservation-tab-toolbar__btn:hover,
.food-order-btn:hover,
.addroom:hover {
  background: var(--brand-primary-hover, #5c368f);
  color: #fff !important;
}

.reservation-empty-row {
  text-align: center;
  color: #64748b;
  font-size: 14px;
  padding: 28px 16px !important;
}

.guest-details-card__form {
  text-align: left;
}

.guest-details-card__field {
  margin-bottom: 8px;
  text-align: left;
}

.guest-details-card__field label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 4px;
  text-align: left;
}

.guest-details-card__input {
  height: 36px;
  padding: 4px 10px;
  font-size: 14px;
  border-radius: 6px;
  text-align: left !important;
}

.guest-details-card__field .vd-picker,
.guest-details-card__field .vd-picker__input,
.guest-details-card__field input {
  width: 100%;
}

.guest-details-card__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 10px;
}

.guest-details-card__hint {
  margin: 8px 0 0;
  font-size: 11px;
  color: #64748b;
}

.reservation-details-page__body {
  padding: 12px 20px 32px;
}

.reservation-details-page__toolbar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 20px;
  gap: 16px;
}

.reservation-details-page__back {
  justify-self: start;
  border: 1px solid #94a3b8;
  background: #fff;
  color: #0f172a;
  border-radius: 8px;
  padding: 8px 18px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.reservation-details-page__heading {
  text-align: left;
}

.reservation-details-page__title {
  margin: 0;
  font-size: 28px;
  font-weight: 700;
  color: #242c67;
}

.reservation-details-page__ref {
  margin: 6px 0 0;
  font-size: 14px;
  color: #64748b;
}

.reservation-details-page__actions--top {
  margin-top: 0;
  align-items: center;
}

.reservation-details-page__actions--top .reservation-details-page__btn {
  min-width: 0;
  padding: 8px 14px;
}

.reservation-details-page__action-btn {
  border: 1px solid #94a3b8;
  background: #fff;
  color: #0f172a;
  border-radius: 8px;
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.reservation-details-page__action-btn--primary {
  background: #16a34a;
  border-color: #15803d;
  color: #fff !important;
}

.reservation-details-page__action-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.summary-card {
  display: flex;
  flex-direction: column;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
  padding: 16px 18px 10px;
  margin-bottom: 16px;
  min-height: 100%;
  overflow: hidden;
  background: #fff;
}

.summary-card__title {
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
  color: #242c67;
  margin: 0 0 8px;
  padding-bottom: 10px;
  border-bottom: 1px solid #eef2f6;
  text-align: left;
}

.summary-card__ref {
  color: #c53a2a;
  font-weight: 700;
  margin-bottom: 0;
}

.payment-status-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
}

.payment-status-badge--reserved {
  background: #fef3c7;
  color: #b45309;
  border: 1px solid #fcd34d;
}

.payment-status-badge--paid {
  background: #dcfce7;
  color: #15803d;
  border: 1px solid #86efac;
}

.payment-status-badge--partial {
  background: #dbeafe;
  color: #1d4ed8;
  border: 1px solid #93c5fd;
}

.payment-status-badge--cancelled {
  background: #fee2e2;
  color: #b91c1c;
  border: 1px solid #fca5a5;
}

.booking-status-row {
  align-items: center;
}

.booking-status-select {
  min-width: 0;
  width: 100%;
  max-width: 160px;
}

.booking-status-manual-hint {
  margin: 8px 0 0;
  font-size: 12px;
  color: #b45309;
  line-height: 1.4;
}

.booking-status-resume-btn {
  margin-top: 8px;
  border: 1px solid #93c5fd;
  background: #eff6ff;
  color: #1d4ed8;
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}

.booking-status-resume-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.food-order-btn {
  min-height: 32px;
  padding: 6px 12px;
  font-size: 12px;
}

.addroom {
  width: auto;
  min-width: 0;
  height: 28px;
  min-height: 28px;
  margin-bottom: 0;
  padding: 0 10px;
  font-size: 12px;
}

.reservation-details-page__actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
  flex-wrap: wrap;
}

.reservation-details-page__btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 180px;
  justify-content: center;
  padding: 10px 18px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
}

.reservation-details-page__btn--print {
  background: var(--brand-primary);
  color: #ffffff;
  border: 1px solid var(--brand-primary);
}

.reservation-details-page__btn--cancel {
  background: #fff5f5;
  color: #dc3545;
  border: 1px solid #fca5a5;
}

.discountButton1 {
  border: none;
  height: 32px;
  border-radius: 10px;
  margin-bottom: 5px 1important;
  background: rgb(72, 185, 7);
  color: white !important;
}
.discountButton {
  border: none;
  height: 32px;
  border-radius: 10px;
  margin-bottom: 5px 1important;
  background-color: var(--brand-primary) !important;
  color: white !important;
}
.buttonpay {
  margin-bottom: 10px;
}
.StatusColor1 {
  height: 23px;
  width: 100px;
  border-radius: 20px;
  background: rgb(209, 44, 44);
  color: #d6d6d6;
  margin-bottom: 10px;
  -webkit-box-shadow: none;
  box-shadow: none;
  border: none;
}
.StatusColor {
  height: 23px;
  width: 100px;
  border-radius: 20px;
  background: rgb(51, 145, 51);
  color: #d6d6d6;
  margin-bottom: 10px;
  -webkit-box-shadow: none;
  box-shadow: none;
  border: none;
}

.payableAmount {
  height: 23px;
  width: 100px;
  border-radius: 20px;
  background: green;
  color: #d6d6d6;
  margin-bottom: 10px;
  -webkit-box-shadow: none;
  box-shadow: none;
  border: none;
}
.ButtonEdit {
  height: 27px;
  width: 28px;
  padding: 4px;
  border-radius: 20px;
  background: rgb(189 188 188);
  margin-bottom: 10px;
  -webkit-box-shadow: none;
  box-shadow: none;
  border: none;
}
.ButtonEdit2 {
  height: 27px;
  width: 28px;
  padding: 4px;
  border-radius: 20px;
  background-color: rgb(255 155 160) !important;
  margin-bottom: 10px;
  -webkit-box-shadow: none;
  box-shadow: none;
  border: none;
}
.ButtonEdit1 {
  height: 27px;
  width: 28px;
  padding: 4px;
  border-radius: 20px;
  background: rgb(49, 194, 109);
  margin-bottom: 10px;
  -webkit-box-shadow: none;
  box-shadow: none;
  border: none;
}
.UpdateButton {
  height: 28px;
  width: 75px;
  margin-left: 5px;
  border-radius: 20px;
  background: rgb(131 216 33);
  margin-bottom: 10px;
  -webkit-box-shadow: none;
  box-shadow: none;
  border: none;
}
.Cancelbutton {
  background: #c53a2a;
  outline: none;
  cursor: pointer;
  display: inline-block;
  text-decoration: none;
  padding: 8px 25px;
  color: rgb(255, 255, 255);
  font-weight: 500;
  text-align: center;
  font-size: 14px;
  font-size: 0.875rem;
  -webkit-transition: all 0.3s ease-in-out;
  transition: all 0.3s ease-in-out;
  border-radius: 3px;
  line-height: normal;
}
.imgContainer {
  max-width: 960px;
  margin: 10px auto;
  padding: 0px;
}
h1 {
  font-size: 20px;
  text-align: center;
  margin: 20px 0 20px;
}
h1 small {
  display: block;
  font-size: 15px;
  padding-top: 8px;
  color: gray;
}
.avatar-upload {
  position: absolute;
}
.avatar-upload .avatar-edit {
  position: absolute;
  right: 12px;
  z-index: 1;
  top: 10px;
}
.avatar-upload .avatar-edit input {
  display: none;
}
.avatar-upload .avatar-edit input + label {
  display: inline-block;
  width: 34px;
  height: 34px;
  margin-bottom: 0;
  border-radius: 100%;
  background: #ffffff;
  border: 1px solid transparent;
  box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.12);
  cursor: pointer;
  font-weight: normal;
  transition: all 0.2s ease-in-out;
}
.avatar-upload .avatar-edit input + label:hover {
  background: #f1f1f1;
  border-color: #d6d6d6;
}
.avatar-upload .avatar-edit input + label:after {
  content: '\f040';
  font-family: 'FontAwesome';
  color: #757575;
  position: absolute;
  top: 10px;
  left: 0;
  right: 0;
  text-align: center;
  margin: auto;
}
.avatar-upload[data-v-067184e0] {
  position: relative;

  margin: -15px auto;
}
.avatar-upload .avatar-preview {
  width: 100px;
  height: 100px;
  position: relative;
  border-radius: 100%;
  border: 6px solid #f8f8f8;
  box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.1);
}
.avatar-upload .avatar-preview > div {
  width: 100%;
  height: 100%;
  border-radius: 100%;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
}

.Pay {
  height: 34px;
  top: 5px;
  margin-top: 5px;
  width: 109px;
  border-radius: 12px;
  color: #ffffff !important;
  background-color: #c53a2a !important;
  margin-bottom: 10px;
  box-shadow: none;
  border: none;
}
.addroom {
  width: auto;
  min-width: 0;
  height: 28px;
  min-height: 28px;
  margin-bottom: 0;
  padding: 0 10px;
  font-size: 12px;
}

.reservation-details-page td.row-actions,
.reservation-details-page th.row-actions {
  width: 1%;
  min-width: 84px;
  white-space: nowrap;
  vertical-align: middle;
  overflow: visible;
}

.reservation-details-page .row-actions a.ButtonEdit,
.reservation-details-page .row-actions a.ButtonEdit1,
.reservation-details-page .row-actions a.ButtonEdit2 {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  min-width: 22px;
  margin: 0 0 0 4px;
  padding: 0;
  font-size: 11px;
  line-height: 1;
  vertical-align: middle;
  box-sizing: border-box;
}

.reservation-details-page .row-actions a:first-child {
  margin-left: 0;
}

.headers {
  text-align: start !important;
  font-size: 13px;
}

.row {
  margin-right: -15px;
  margin-left: -15px;
}

.width-100perc {
  width: 100% !important;
}

.modal-body input[type='text'] {
  margin-bottom: 5px;
}

input[type='text'],
input[type='password'],
textarea,
input[type='tel'],
input[type='email'],
input[type='number'] {
  padding: 7px;
  border: 1px #e5e5e5 solid;
  margin-top: 2px;
  border-radius: 3px;
  /* border-bottom: #e5e5e5 2px solid; */
}

.margin-bottom-0px {
  margin-bottom: 0px !important;
}

.margin-top-15px {
  margin-top: 15px !important;
}

.card {
  border: 1px solid #f0f0f0;
  margin-bottom: 1.875rem;
}

.card {
  position: relative;
  display: -ms-flexbox;
  display: flex;
  -ms-flex-direction: column;
  flex-direction: column;
  min-width: 0;
  word-wrap: break-word;
  background-color: #fff;
  background-clip: border-box;
  border: 1px solid rgba(0, 0, 0, 0.125);
  border-radius: 0.25rem;
}

.card-body {
  padding: 1.5rem;
}

.card-body {
  -ms-flex: 1 1 auto;
  flex: 1 1 auto;
  padding: 1.25rem;
}

.table-responsive {
  white-space: nowrap;
}

.table-responsive {
  display: block;
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

div.table-responsive > div.dataTables_wrapper > div.row {
  margin: 0;
}

div.dataTables_wrapper div.dataTables_length label {
  font-weight: normal;
  text-align: left;
  white-space: nowrap;
}

label {
  display: inline-block;
  margin-bottom: 0.5rem;
}

div.dataTables_wrapper div.dataTables_length select {
  width: auto;
  display: inline-block;
}

div.table-responsive
  > div.dataTables_wrapper
  > div.row
  > div[class^='col-']:last-child {
  padding-right: 0;
}

div.table-responsive > div.dataTables_wrapper > div.row {
  margin: 0;
}

div.table-responsive
  > div.dataTables_wrapper
  > div.row
  > div[class^='col-']:last-child {
  padding-right: 0;
}

div.table-responsive
  > div.dataTables_wrapper
  > div.row
  > div[class^='col-']:first-child {
  padding-left: 0;
}

.table.dataTable {
  border-collapse: collapse !important;
}

table.dataTable {
  clear: both;
  margin-top: 6px !important;
  margin-bottom: 6px !important;
  max-width: none !important;
  border-collapse: separate !important;
  border-spacing: 0;
}

.table {
  color: #333;
  max-width: 100%;
  margin-bottom: 0;
  width: 100%;
}

.table {
  width: 100%;
  margin-bottom: 1rem;
  color: #212529;
}

.table thead {
  background-color: #f8f9fa;
  border-bottom: 1px solid rgba(0, 0, 0, 0.03);
}

tr {
  display: table-row;
  vertical-align: inherit;
  border-color: inherit;
}

table.dataTable thead > tr > th.sorting_asc {
  padding-right: 30px;
  cursor: pointer;
  position: relative;
}

.table.table-center th {
  vertical-align: middle;
}

.table thead tr th {
  font-weight: 600;
  border: 0;
}

table.dataTable th {
  -webkit-box-sizing: content-box;
  box-sizing: content-box;
}

.table thead th {
  vertical-align: bottom;
  border-bottom: 2px solid #dee2e6;
}

table.dataTable thead > tr > th.sorting {
  padding-right: 30px;
  cursor: pointer;
  position: relative;
}

.table.table-center th {
  vertical-align: middle;
}

.table thead tr th {
  font-weight: 600;
  border: 0;
}

table.dataTable th {
  -webkit-box-sizing: content-box;
  box-sizing: content-box;
}

.table thead th {
  vertical-align: bottom;
  border-bottom: 2px solid #dee2e6;
}

.table th {
  padding: 0px;
  vertical-align: top;
  border-top: 1px solid #dee2e6;
}

th {
  text-align: inherit;
}

.table tbody tr {
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.table.table-center td {
  vertical-align: middle;
}

table.table td h2.table-avatar {
  align-items: center;
  display: inline-flex;
  font-size: inherit;
  font-weight: 400;
  margin: 0;
  padding: 0;
  vertical-align: middle;
  white-space: nowrap;
}

table.table td h2 {
  display: inline-block;
  font-size: inherit;
  font-weight: 400;
  margin: 0;
  padding: 0;
  vertical-align: middle;
}

h2 {
  font-family: 'Mada', sans-serif;
  margin-top: 0;
}

h2 {
  font-size: 2rem;
}

.table.table-center td {
  vertical-align: middle;
}

table.dataTable td {
  -webkit-box-sizing: content-box;
  box-sizing: content-box;
}

.table td {
  padding: 0px;
  vertical-align: top;
  border-top: 3px solid #dee2e6;
}

table.table td h2.table-avatar {
  align-items: center;
  display: inline-flex;
  font-size: inherit;
  font-weight: 400;
  margin: 0;
  padding: 0;
  vertical-align: middle;
  white-space: nowrap;
}

table.table td h2 a {
  color: #333;
}

.avatar-sm {
  width: 2.5rem;
  height: 2.5rem;
}

.avatar {
  position: relative;
  display: inline-block;
  width: 3rem;
  height: 3rem;
}

a {
  transition: all 0.4s ease;
  -moz-transition: all 0.4s ease;
  -o-transition: all 0.4s ease;
  -ms-transition: all 0.4s ease;
  -webkit-transition: all 0.4s ease;
}

a {
  color: #007bff;
  text-decoration: none;
  background-color: transparent;
}

.avatar-sm {
  width: 2.5rem;
  height: 2.5rem;
}

table.table td h2 a {
  color: #333;
}

.mr-2 {
  margin-right: 0.5rem !important;
}

.table th {
  padding: 0px;
  vertical-align: top;
  border-top: 1px solid #dee2e6;
}

th {
  text-align: inherit;
}

.top {
  margin-top: -195px !important;
}

.vm--modal {
  position: relative;
  overflow: visible !important;
  box-sizing: border-box;
  background-color: white;
  border-radius: 3px;
  box-shadow: 0 20px 60px -2px rgba(27, 33, 58, 0.4);
}

.modal_header,
.modal_footer {
  width: 100% !important;

  left: 0px !important;
}

* {
  list-style: none;
}

.booked {
  filter: grayscale(1);
}

.checkout {
  width: 100%;
  height: 38px;
  color: aquamarine;
  background-color: #ffffff;
  border-radius: 31px;
}
.margin_60_40 {
  padding-top: 10px;
  padding-bottom: 40px;
}

.checkout:active {
  width: 100%;
  height: 38px;
  color: aquamarine !important;
  background-color: #ffffff !important;
  border-radius: 31px;
}
.mb {
  margin-bottom: 20px !important;
}
</style>
<style lang="scss" scoped>
label.nav,
a.ButtonEdit,
a.ButtonEdit1,
a.ButtonEdit2 {
  cursor: pointer;
  user-select: none;
  text-decoration: none;
}

.layout {
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: auto;
  width: 100%;
  overflow: visible;
}

input[type='radio'] {
  display: none;
}

label.nav {
  display: none;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  user-select: none;
  min-height: 42px !important;
  padding: 0 12px;
  border-radius: 10px 10px 0 0;
  border: 1px solid #cbd5e1;
  border-bottom: none;
  background: #f1f5f9;
  color: #334155 !important;
  font-size: 14px;
  font-weight: 700;
  transition: background 0.2s ease, color 0.2s ease;

  span {
    color: inherit !important;
    padding-left: 0;
    position: relative;
  }
}

label.nav[for='Guest'] {
  grid-column: 1;
  grid-row: 1;
}

label.nav[for='Orders'] {
  grid-column: 2;
  grid-row: 1;
}

label.nav[for='Payments'] {
  grid-column: 3;
  grid-row: 1;
}

#Guest:checked ~ label.nav[for='Guest'],
#Orders:checked ~ label.nav[for='Orders'],
#Payments:checked ~ label.nav[for='Payments'] {
  background: var(--brand-primary);
  border-color: var(--brand-primary);
  color: #ffffff !important;

  span {
    color: #ffffff !important;
  }
}

.page {
  display: block;
  position: relative;
  padding: 0 0 12px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.rd-section--orders {
  order: 1;
}

.rd-section--rooms {
  order: 2;
}

.rd-section--payments {
  order: 3;
}

.rd-section-title {
  margin: 0;
  padding: 16px 16px 12px;
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  border-bottom: 1px solid #e5e7eb;
  text-align: left;
}

.rd-history {
  margin-top: 16px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
}

.rd-history td,
.rd-history th {
  white-space: nowrap;
  vertical-align: middle;
}

.rd-history td:nth-child(3) {
  white-space: normal;
}

.layout .card,
.layout .card-body {
  border: 0 !important;
  border-radius: 0;
  box-shadow: none;
  background: transparent;
  padding: 0;
  margin: 0;
  width: 100%;
}

.layout .table-responsive,
.rd-history .table-responsive {
  display: block;
  width: 100%;
  padding: 0 16px 12px;
  white-space: normal;
  overflow-x: auto;
  box-sizing: border-box;
}

.layout table.datatable,
.rd-history table.datatable {
  width: auto;
  min-width: 0;
  table-layout: auto;
  border-collapse: collapse;
}

.layout table.datatable th,
.layout table.datatable td,
.rd-history table.datatable th,
.rd-history table.datatable td {
  width: auto;
  text-align: left;
  vertical-align: middle;
  padding: 8px 12px;
  box-sizing: border-box;
  white-space: nowrap;
  word-break: normal;
  font-size: 13px;
  line-height: 1.35;
}

.layout table.datatable thead th,
.rd-history table.datatable thead th {
  font-weight: 600;
  font-size: 13px;
  color: #6b7280;
  background: #fff;
  border-top: 0;
  border-bottom: 1px solid #e5e7eb;
}

.layout table.datatable .rd-col-actions,
.layout table.datatable td.row-actions {
  width: 1%;
  white-space: nowrap;
}

.layout table.datatable td.rd-col-wrap,
.rd-history table.datatable td.rd-col-wrap {
  white-space: normal;
  max-width: 240px;
}

.layout table.datatable .headers {
  width: auto;
  max-width: 220px;
  padding: 0;
  margin: 0;
}

.reservation-empty-row {
  background: #f3f4f6;
  color: #9ca3af;
  text-align: center !important;
  font-size: 14px;
  padding: 16px 12px !important;
}

.layout table.datatable th:first-child,
.layout table.datatable td:first-child,
.rd-history table.datatable th:first-child,
.rd-history table.datatable td:first-child {
  padding-left: 0;
}

.rd-history__pager {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 8px 16px 12px;
  border-top: 1px solid #e5e7eb;
}

.rd-history__count {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
}

.rd-history__pages {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}

.rd-history__page {
  height: 28px;
  min-width: 28px;
  padding: 0 10px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #ffffff;
  color: #0f172a !important;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}

.rd-history__page:disabled {
  opacity: 0.45;
  cursor: default;
}

.rd-history__page--active {
  background: #058397;
  border-color: #058397;
  color: #ffffff !important;
}

.layout .page-contents .row {
  margin-left: 0;
  margin-right: 0;
}

.layout .page-contents [class*='col-'] {
  padding-left: 0;
  padding-right: 0;
}

.rd-pay-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 16px 8px;
}

.rd-pay-summary {
  display: grid;
  grid-template-columns: max-content max-content;
  column-gap: 28px;
  row-gap: 6px;
  align-items: center;
  text-align: left;
}

.rd-pay-summary__label {
  text-align: left;
  font-weight: 700;
  font-size: 14px;
  color: #1e293b;
}

.rd-pay-summary__value {
  text-align: right;
  font-weight: 700;
  font-size: 14px;
  color: rgb(198, 49, 115);
}

.rd-pay-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}
.card[data-v-067184e0] {
  position: relative;
  display: -ms-flexbox;
  display: -webkit-box;
  display: flex;
  -ms-flex-direction: column;
  -webkit-box-orient: vertical;
  -webkit-box-direction: normal;
  flex-direction: column;
  min-width: 0;
  word-wrap: break-word;
  background-color: #fff;
  background-clip: border-box;
  border: 5px solid rgba(0, 0, 0, 0.125);
  border-radius: 1.25rem;
}

.page-contents > * {
  opacity: 1;
  transform: none;
  margin-top: 0;

  @for $c from 1 through 20 {
    &:nth-child(#{$c}) {
      transition-delay: 0.2s + ($c * 0.2s);
    }
  }
}

input[type='radio'] + .page,
input[type='radio']:checked + .page {
  transform: none;

  .page-contents > * {
    opacity: 1;
    transform: none;
  }
}

.page-contents {
  max-width: 100%;
  width: 100%;
  margin: 0 auto;
}

.booknow {
  width: 120px;
  height: 30px;
  border: none;
  background: #ce0f0f;
  color: white !important;
}
.roomarea {
  max-height: 222px;
  width: 108%;
  overflow-x: scroll;
  margin-top: 8px;
  margin-bottom: 2px;
}
</style>
